/**
 * Changes to the events described here should be represented on the Help Center page:
 * https://support.mobi2go.com/hc/en-us/articles/360020809752-Facebook-Pixel
 *
 * Refer: https://developers.facebook.com/docs/facebook-pixel/implementation/conversion-tracking
 * Note that we support a fb_ids object created elsewhere to map menu_product.names to ids, e.g.
 * var fb_ids = {
 *   "Bio Fuel":'2010',
 *   "Peanut Piston":'002',
 *   ...
 * }
 */

/**-----------------------------------------------------------------------------
 * Load in production only, or if forced
 */
import _ from 'lodash';

const load_analytics = /(\?|&)load_analytics/.test(document.location.search);
if (Mobi2Go.app.base_url === 'https://www.mobi2go.com' || load_analytics) {
    Mobi2Go.onReady(load);
}

function load() {
    const { Order } = Mobi2Go;
    const { Analytics } = Mobi2Go;
    const E = Analytics.EVENTS;

    /**-------------------------------------------------------------------------
     * Script Setup
     */
    const facebook_pixel_id = Mobi2Go.headoffice.analytics_facebook_pixel_id;
    window.fb_ids = window.fb_ids || [];

    if (!facebook_pixel_id) {
        return;
    }

    (function() {
        if (window.fbq) {
            window.fbq_loaded_by_mobi2go =
                window.fbq_loaded_by_mobi2go || false;
            return;
        }

        !(function(f, b, e, v, n, t, s) {
            if (f.fbq) return;
            n = f.fbq = function() {
                n.callMethod
                    ? n.callMethod.apply(n, arguments)
                    : n.queue.push(arguments);
            };
            if (!f._fbq) f._fbq = n;
            n.push = n;
            n.loaded = !0;
            n.version = '2.0';
            n.queue = [];
            t = b.createElement(e);
            t.async = !0;
            t.src = v;
            s = b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t, s);
        })(
            window,
            document,
            'script',
            'https://connect.facebook.net/en_US/fbevents.js'
        );

        window.fbq_loaded_by_mobi2go = true;
    })();

    /**-------------------------------------------------------------------------
     * Abstraction
     */

    function startSession() {
        if (window.fbq && window.fbq_loaded_by_mobi2go) {
            // Some clients provide the Facebook pixel and call the init event
            // in their HTML templates, however if loading via the app
            // we need to do the initialisation.
            fbq('init', facebook_pixel_id);
            fbq('track', 'PageView');
        }

        fbq('trackCustom', 'country', {
            country_iso: Mobi2Go.headoffice.country_iso3166_alpha2,
        });

        fbq('trackCustom', 'platform', {
            platform: Mobi2Go.app.getPlatform(),
        });
    }

    function loadLastOrder(last_order_state) {
        if (!window.fbq) {
            return;
        }

        fbq('trackCustom', 'LoadLastOrder', {
            last_order_id: last_order_state.id,
        });
    }

    function identifyLocation(location) {
        if (!window.fbq) {
            return;
        }

        if (location) {
            fbq('track', 'FindLocation', {
                location_name: location.name,
            });
        }
    }

    function identifyUser(customer) {
        if (!window.fbq) {
            return;
        }

        if (customer) {
            fbq('trackCustom', 'IdentifyUser', {
                customer_id: customer.id,
            });
        }
    }

    function viewMenu(menu) {
        if (!window.fbq) {
            return;
        }

        const content_ids = _contentIds(menu.getCategoryProducts().toArray());

        fbq('trackCustom', 'ViewMenu', {
            menu_name: menu.name,
            content_ids,
        });
    }

    function viewCategory(category) {
        if (!window.fbq) {
            return;
        }

        const content_ids = _contentIds(category.getProducts().toArray());

        fbq('trackCustom', 'ViewCategory', {
            content_category: category.name,
            content_ids,
        });
    }

    function addProductToOrder(product) {
        if (!window.fbq) {
            return;
        }

        let content_id;
        if (window.fb_ids[product.menu_product.name]) {
            content_id = window.fb_ids[product.menu_product.name];
        } else {
            content_id = product.menu_product.id;
        }

        fbq('track', 'AddToCart', {
            content_ids: [content_id],
            content_name: product.menu_product.name,
            content_type: 'product',
            currency: Mobi2Go.headoffice.currency_abrv,
            value: product.price[0],
        });
    }

    function editProductOnOrder(product) {
        if (!window.fbq) {
            return;
        }

        let content_id;
        if (window.fb_ids[product.menu_product.name]) {
            content_id = window.fb_ids[product.menu_product.name];
        } else {
            content_id = product.menu_product.id;
        }

        fbq('trackCustom', 'EditProduct', {
            product_name: product.menu_product.name,
            content_ids: [content_id],
        });
    }

    function removeProductFromOrder(product) {
        if (!window.fbq || Order.isResetting()) {
            return;
        }

        let content_id;
        if (window.fb_ids[product.menu_product.name]) {
            content_id = window.fb_ids[product.menu_product.name];
        } else {
            content_id = product.menu_product.id;
        }

        fbq('trackCustom', 'RemoveProduct', {
            product_name: product.menu_product.name,
            content_ids: [content_id],
        });
    }

    function checkout() {
        if (!window.fbq) {
            return;
        }

        const contents = _contents(Order);
        const menu_products = Order.getProducts().map(function(product) {
            return product.menu_product;
        });
        const content_ids = _contentIds(menu_products);

        fbq('track', 'InitiateCheckout', {
            content_ids,
            currency: Mobi2Go.headoffice.currency_abrv,
            value: Order.getTotal()
                .getTaxExclusivePrice()
                .toFloat(),
            contents,
        });
    }

    function startPayment(order) {
        if (!window.fbq) {
            return;
        }

        fbq('trackCustom', 'StartPayment', {
            order_id: order.getId(),
        });
    }

    function saveCreditCard() {
        if (!window.fbq) {
            return;
        }

        fbq('trackCustom', 'SaveCreditCard', {});
    }

    function confirmedOrder(order) {
        if (!window.fbq) {
            return;
        }

        const contents = _contents(order);
        const menu_products = order.getProducts().map(function(product) {
            return product.menu_product;
        });
        const content_ids = _contentIds(menu_products);

        fbq('track', 'Purchase', {
            content_ids,
            content_type: 'product',
            currency: Mobi2Go.headoffice.currency_abrv,
            value: order
                .getTotal()
                .getTaxExclusivePrice()
                .toFloat(),
            contents,
            tax: order
                .getTotal()
                .getTaxPrice()
                .toFloat(),
            shipping: order.getDeliveryFee().toFloat(),
            coupon: order.getVoucherCode(),
        });
    }

    function completeRegistration() {
        if (!window.fbq) {
            return;
        }

        fbq('track', 'CompleteRegistration', {});
    }

    function _contentIds(menu_products) {
        const content_ids = [];
        let content_id;

        _.each(menu_products, function(menu_product) {
            if (window.fb_ids[menu_product.name]) {
                content_id = window.fb_ids[menu_product.name];
            } else {
                content_id = menu_product.id;
            }

            content_ids.push(content_id);
        });

        return content_ids;
    }

    function _contents(order) {
        const contents = [];
        let content_id;

        _.each(order.getProducts(), function(product) {
            if (window.fb_ids[product.menu_product.name]) {
                content_id = window.fb_ids[product.menu_product.name];
            } else {
                content_id = product.id;
            }

            contents.push({
                id: content_id,
                name: product.menu_product.name,
                item_price: product.price.toFloat(),
                quantity: product.quantity,
                variant: product.size ? product.size.menu_modifier.name : null,
                category: product.menu_product.category
                    ? product.menu_product.category.name
                    : null,
                position: product.menu_product.category_list_position
                    ? product.menu_product.category_list_position
                    : null,
            });
        });

        return contents;
    }

    /**-------------------------------------------------------------------------
     * Application Glue
     */

    startSession();

    Analytics.bind({
        LAST_ORDER_LOADED: loadLastOrder,
        IDENTIFY_LOCATION: identifyLocation,
        IDENTIFY_CUSTOMER: identifyUser,
        VIEWED_PRODUCT_MENU: viewMenu,
        VIEWED_PRODUCT_CATEGORY: viewCategory,
        ADDED_PRODUCT: addProductToOrder,
        EDITED_PRODUCT: editProductOnOrder,
        REMOVED_PRODUCT: removeProductFromOrder,
        STARTED_PAYMENT: startPayment,
        CREDIT_CARD_SAVED: saveCreditCard,
        CONFIRMED_ORDER: confirmedOrder,
        STARTED_CHECKOUT: checkout,
        REGISTERED_CUSTOMER: completeRegistration,
    });
}
